import {dataTestId} from '@hconnect/uikit'
import {Add, Delete} from '@mui/icons-material'
import {Box, Fab, Stack, useMediaQuery, useTheme} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {StrengthTargetContentArea} from './components/StrengthTargetContentArea'
import {StrengthTargetFormDialog} from './components/StrengthTargetFormDialog'
import {StrengthTargetMobileHeader} from './components/StrengthTargetMobileHeader'
import {StrengthTargetTabsWithActions} from './components/StrengthTargetTabsWithActions'
import {useStrengthTargetsManagement} from './hooks/useStrengthTargetsManagement'
import {DayPeriod, StrengthTarget} from './types'

import {useConfirmDialog} from '@settings/modules/common/providers/ConfirmDialogProvider'

interface StrengthTargetsProps {
  isReadOnly?: boolean
  timezone: string
}

export const StrengthTargets: React.FC<StrengthTargetsProps> = ({isReadOnly = false, timezone}) => {
  const {t} = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const {openDialog} = useConfirmDialog()

  const [selectedTab, setSelectedTab] = useState<DayPeriod | 'overview'>('overview')

  const {
    targets,
    backendTargets,
    isFormOpen,
    editingTarget,
    openNewTargetForm,
    openEditForm,
    closeForm,
    saveTarget,
    confirmDelete,
    getTargetsForDay
  } = useStrengthTargetsManagement(timezone)

  const targetsForSelectedDay = selectedTab !== 'overview' ? getTargetsForDay(selectedTab) : []
  const hasTargetsForSelectedDay = targetsForSelectedDay.length > 0

  const shouldShowFloatingButton =
    isMobile && !isReadOnly && selectedTab !== 'overview' && hasTargetsForSelectedDay

  const handleDeleteTarget = (target: StrengthTarget) => {
    openDialog({
      title: t('materialsSettings.strengthTargets.deleteTarget'),
      description: t('materialsSettings.strengthTargets.deleteTargetConfirmation'),
      mainAction: {
        color: 'error',
        text: t('common.delete'),
        icon: <Delete />,
        onAction: () => {
          confirmDelete(target)
        }
      },
      testId: 'delete_target_dialog'
    })
  }

  return (
    <Box sx={{width: '100%', position: 'relative'}} {...dataTestId('strength_targets_container')}>
      <Stack spacing={isMobile ? 2 : 3}>
        {isMobile && (
          <StrengthTargetMobileHeader
            selectedTab={selectedTab}
            hasTargetsForSelectedDay={hasTargetsForSelectedDay}
            isReadOnly={isReadOnly}
            onNewTarget={openNewTargetForm}
          />
        )}

        <StrengthTargetTabsWithActions
          selectedTab={selectedTab}
          hasTargetsForSelectedDay={hasTargetsForSelectedDay}
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          onTabChange={setSelectedTab}
          onNewTarget={openNewTargetForm}
        />

        <StrengthTargetContentArea
          selectedTab={selectedTab}
          targets={targets}
          targetsForSelectedDay={targetsForSelectedDay}
          hasTargetsForSelectedDay={hasTargetsForSelectedDay}
          isReadOnly={isReadOnly}
          timezone={timezone}
          onEditTarget={openEditForm}
          onDeleteTarget={handleDeleteTarget}
          onNewTarget={openNewTargetForm}
        />
      </Stack>

      {shouldShowFloatingButton && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={openNewTargetForm}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}
          {...dataTestId('new_target_fab_mobile')}
        >
          <Add />
        </Fab>
      )}

      <StrengthTargetFormDialog
        open={isFormOpen}
        target={editingTarget}
        defaultDayPeriod={selectedTab !== 'overview' ? selectedTab : 1}
        timezone={timezone}
        onClose={closeForm}
        onSave={saveTarget}
        existingTargets={targets}
        backendTargets={backendTargets}
      />
    </Box>
  )
}
